import React, { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2, XCircle, ExternalLink, RefreshCw, ShieldCheck,
  Mail, Calendar, Globe, BadgeCheck, AlertCircle, Search, User,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "cancelled", label: "Cancelled" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso?.slice?.(0, 19) || "—";
  }
};

// --------------- Single claim card ---------------
const ClaimCard = ({ claim, onApprove, onReject, busyId }) => {
  const [rejectReason, setRejectReason] = useState("");
  const profile = claim.user_profile || {};
  const name = claim.artist_name || claim.catalog_artist_name || "(unknown)";
  const status = claim.status || "pending";
  const isPending = status === "pending";

  const proofUrl = claim.apple_music_url || claim.proof_url || claim.social_url;

  return (
    <div
      data-testid={`claim-card-${claim.claim_id}`}
      className="rounded-2xl bg-white/[0.03] border border-white/8 p-4 sm:p-5 hover:border-white/20 transition"
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Submitter avatar */}
        <div className="flex items-center gap-3 sm:flex-col sm:items-center shrink-0">
          {profile.picture ? (
            <img
              src={profile.picture}
              alt=""
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover ring-2 ring-white/10"
            />
          ) : (
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/30 flex items-center justify-center text-lg font-semibold">
              {(profile.name || claim.user_email || "?")[0]?.toUpperCase()}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-display text-lg sm:text-xl font-semibold leading-tight">
              {name}
            </h3>
            {claim.itunes_artist_id && (
              <span className="text-[10px] uppercase tracking-wider bg-white/8 text-white/70 px-1.5 py-0.5 rounded">
                iTunes #{claim.itunes_artist_id}
              </span>
            )}
            {claim.catalog_source && (
              <span className="text-[10px] uppercase tracking-wider bg-[#E2FF31]/15 text-[#E2FF31] px-1.5 py-0.5 rounded font-bold">
                {claim.catalog_source}
              </span>
            )}
            {claim.fast_tracked && (
              <span className="text-[10px] uppercase tracking-wider bg-[#FF2E00]/20 text-[#FF2E00] px-1.5 py-0.5 rounded font-bold">
                FAST-TRACKED
              </span>
            )}
          </div>

          <div className="text-xs text-white/55 flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
            <span className="flex items-center gap-1">
              <User size={11} /> {profile.name || claim.user_name || "Anon"}
            </span>
            <span className="flex items-center gap-1">
              <Mail size={11} /> {claim.user_email || profile.email || "no-email"}
            </span>
            {profile.premium_tier && (
              <span className="flex items-center gap-1 capitalize">
                {profile.premium_tier === "artist" ? (
                  <BadgeCheck size={11} className="text-[#E2FF31]" />
                ) : (
                  <ShieldCheck size={11} />
                )}
                {profile.premium_tier}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar size={11} /> {formatDate(claim.created_at)}
            </span>
          </div>

          {claim.official_email && claim.official_email !== claim.user_email && (
            <p className="text-xs text-white/55 mb-2">
              Contact: <span className="text-white/85">{claim.official_email}</span>
            </p>
          )}

          {proofUrl && (
            <a
              href={proofUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`claim-proof-link-${claim.claim_id}`}
              className="inline-flex items-center gap-1.5 text-xs text-[#E2FF31] hover:underline mb-2 break-all"
            >
              <Globe size={12} /> {proofUrl.slice(0, 80)}
              {proofUrl.length > 80 ? "…" : ""}
              <ExternalLink size={10} />
            </a>
          )}

          {claim.statement && (
            <p className="text-sm text-white/80 mt-2 leading-relaxed bg-white/[0.03] rounded-lg px-3 py-2 border border-white/5">
              {claim.statement}
            </p>
          )}

          {status === "rejected" && claim.rejection_reason && (
            <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-[#FF2E00]/10 border border-[#FF2E00]/20 text-xs">
              <AlertCircle size={14} className="text-[#FF2E00] mt-0.5 flex-shrink-0" />
              <p className="text-white/70">
                <span className="font-medium text-[#FF2E00]">Reason:</span> {claim.rejection_reason}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        {isPending && (
          <div className="flex sm:flex-col gap-2 sm:items-stretch shrink-0">
            <button
              data-testid={`claim-approve-${claim.claim_id}`}
              disabled={busyId === claim.claim_id}
              onClick={() => onApprove(claim.claim_id)}
              className="flex-1 sm:flex-none bg-[#E2FF31] hover:opacity-90 text-black font-semibold rounded-full px-4 py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 transition"
            >
              <CheckCircle2 size={14} /> Approve
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid={`claim-reject-${claim.claim_id}`}
                  disabled={busyId === claim.claim_id}
                  className="flex-1 sm:flex-none bg-white/8 hover:bg-[#FF2E00]/15 text-white hover:text-[#FF2E00] rounded-full px-4 py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 transition"
                >
                  <XCircle size={14} /> Reject
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0a0a0a] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Reject this claim?</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/60">
                    Optionally provide a reason — it&apos;ll be visible to {profile.name || claim.user_email}.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <textarea
                  data-testid={`claim-reject-reason-${claim.claim_id}`}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value.slice(0, 300))}
                  rows={3}
                  placeholder="e.g. Could not verify ownership"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 transition resize-none my-2"
                />
                <AlertDialogFooter>
                  <AlertDialogCancel
                    onClick={() => setRejectReason("")}
                    className="bg-white/5 hover:bg-white/15 border-white/10 text-white"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      onReject(claim.claim_id, rejectReason);
                      setRejectReason("");
                    }}
                    data-testid={`claim-confirm-reject-${claim.claim_id}`}
                    className="bg-[#FF2E00] hover:bg-[#E62900] text-white"
                  >
                    Reject claim
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
        {!isPending && (
          <span
            className={`text-xs uppercase font-bold px-3 py-1.5 rounded-full self-start sm:self-center ${
              status === "approved"
                ? "bg-[#E2FF31]/15 text-[#E2FF31]"
                : status === "rejected"
                ? "bg-[#FF2E00]/15 text-[#FF2E00]"
                : "bg-white/8 text-white/60"
            }`}
          >
            {status}
          </span>
        )}
      </div>
    </div>
  );
};

// --------------- Main page ---------------
const AdminClaims = () => {
  const { user, login } = useAuth();
  const [tab, setTab] = useState("pending");
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openMode, setOpenMode] = useState(false);
  const [query, setQuery] = useState("");

  // Verify admin access first
  useEffect(() => {
    if (!user) {
      setAuthChecked(true);
      return;
    }
    api
      .get("/admin/whoami")
      .then((r) => {
        setIsAdmin(!!r.data?.is_admin);
        setOpenMode(!!r.data?.admin_open_mode);
      })
      .catch(() => setIsAdmin(false))
      .finally(() => setAuthChecked(true));
  }, [user]);

  const load = useCallback(async () => {
    if (!user || !isAdmin) return;
    setLoading(true);
    try {
      const r = await api.get(`/uploads/admin/artist-claims`, { params: { status: tab } });
      setClaims(r.data?.results || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access only");
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (claimId) => {
    setBusyId(claimId);
    try {
      await api.post(`/uploads/admin/artist-claims/${claimId}/approve`);
      toast.success("Claim approved — artist verified ✓");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (claimId, reason) => {
    setBusyId(claimId);
    try {
      await api.post(`/uploads/admin/artist-claims/${claimId}/reject`, { reason });
      toast.success("Claim rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  // Gate
  if (!user) {
    return (
      <div data-testid="admin-claims-page" className="flex flex-col items-center justify-center py-24 text-center px-4">
        <ShieldCheck size={36} className="mx-auto text-white/30 mb-4" />
        <h2 className="font-display text-3xl mb-2">Admin sign-in required</h2>
        <button
          onClick={login}
          data-testid="admin-claims-signin"
          className="mt-4 bg-white text-black rounded-full px-6 py-2.5 font-semibold"
        >
          Sign in
        </button>
      </div>
    );
  }
  if (!authChecked) {
    return <div className="py-16 text-center text-white/40">Checking access…</div>;
  }
  if (!isAdmin) {
    return (
      <div data-testid="admin-claims-page" className="py-24 text-center px-4">
        <ShieldCheck size={36} className="mx-auto text-[#FF2E00]/70 mb-4" />
        <h2 className="font-display text-3xl mb-2">Admin access required</h2>
        <p className="text-white/55 text-sm max-w-md mx-auto">
          Your account isn&apos;t on the admin list. Ask the owner to add your email to the
          <code className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs">ADMIN_EMAILS</code>
          env var.
        </p>
      </div>
    );
  }

  // Search filter (client-side; queues are small)
  const filtered = !query.trim()
    ? claims
    : claims.filter((c) => {
        const q = query.toLowerCase();
        return (
          (c.artist_name || c.catalog_artist_name || "").toLowerCase().includes(q) ||
          (c.user_email || "").toLowerCase().includes(q) ||
          (c.user_profile?.name || "").toLowerCase().includes(q) ||
          (c.itunes_artist_id || "").includes(q) ||
          (c.catalog_artist_id || "").includes(q)
        );
      });

  return (
    <div data-testid="admin-claims-page" className="pb-24 max-w-4xl mx-auto px-1 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
          <ShieldCheck size={12} className="text-[#E2FF31]" /> Admin Dashboard
          {openMode && (
            <span className="text-[10px] bg-[#FF2E00]/15 text-[#FF2E00] px-1.5 py-0.5 rounded uppercase">
              open mode
            </span>
          )}
        </p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter">
            Artist Claim Requests
          </h1>
          <button
            onClick={load}
            data-testid="admin-claims-refresh"
            className="p-2.5 rounded-full bg-white/5 hover:bg-white/15 active:scale-95 transition"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Tabs with counts */}
      <div className="flex gap-2 mb-5 overflow-x-auto hide-scrollbar -mx-1 px-1 pb-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition flex items-center gap-1.5 ${
              tab === t.key
                ? "bg-white text-black border-white"
                : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
            }`}
          >
            {t.label}
            {counts[t.key] != null && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  tab === t.key ? "bg-black/15 text-black" : "bg-white/10 text-white/60"
                }`}
              >
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="text"
          data-testid="admin-claims-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by artist name, email, or ID…"
          className="w-full bg-white/[0.03] border border-white/8 rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-white/25 transition"
        />
      </div>

      {/* List */}
      {loading ? (
        <p className="text-white/40 py-12 text-center">Loading claims…</p>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <BadgeCheck size={32} className="mx-auto text-white/20 mb-3" />
          <p className="text-white/50 text-sm">
            {query.trim() ? "No matches for your filter." : `No ${tab} claims right now.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <ClaimCard
              key={c.claim_id}
              claim={c}
              onApprove={approve}
              onReject={reject}
              busyId={busyId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminClaims;
